import { FC } from 'react';
import styled from 'styled-components';
import { LinkButton } from '../LinkButton/LinkButton';
import { AccessibleRoleProp } from './List';

const ProgressListItemElement = styled.div`
  padding: 1rem 2rem;
  margin-bottom: 4rem;
  text-align: left;
`;

const TextElement = styled.span`
  line-height: 2.4rem;
  font-weight: 500;
  font-size: 1.4rem;
`;

const ProgressLineContainer = styled.div`
  height: 7px;
  width: 100%;
  ${({ theme }) => `background-color: ${theme.vars.newColor.lightGS1};`}
  margin: 7px 0;
`;

const ProgressLineFill = styled.div<{ fill: number; hasFailed: boolean }>`
  height: 100%;
  ${({ hasFailed, fill, theme }) => {
    if (hasFailed) {
      return `
      width: 100%;
      background-color: ${theme.vars.newColor.red2};
    `;
    }

    let background;
    if (fill === 100) {
      background = theme.vars.newColor.green2;
    } else if (fill >= 80) {
      background = theme.vars.newColor.orange2;
    } else {
      background = theme.vars.newColor.darkGS2;
    }

    return `
      width: ${fill}%;
      background-color: ${background};
    `;
  }}
`;

const ProgressContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export interface Retry {
  /**
   * Optional retry message. N.B. this is display right of the failure message
   * @default "Retry"
   */
  message?: string;
  /**
   * Mandatory callback applied on click of the Retry message text
   */
  callback: () => void;
}

export interface Failure {
  /**
   * Mandatory - can be an empty string if required. N.B. appears to the left of any Retry message
   */
  message: string;
  /**
   * Optional retry object
   */
  retry?: Retry;
}

export interface Rate {
  totalBytes: number;
  start: Date;
}

export interface ProgressItemListProps extends AccessibleRoleProp {
  /**
   * Mandatory - subject to visualise progress for
   */
  subject: string;
  /**
   * Mandatory - progress percentage to visualise
   */
  progress: number;
  /**
   * Optional rate - use if you wish to show transfer rate
   * ```{
   *   totalBtyes: string,
   *   start: Date,
   * }```
   */
  rate?: Rate;
  /**
   * Optional failure - passing a failure object will override the component as "failed".
   * 0% progress will show, bar will be filled and use an error colour. Failure object can take an optional
   * Retry object (see Retry interface for more detail). Note component doesn't attempt to punctuate between
   * messages, they are just joined by a space.
   * ```{
   *   message: string,
   *   retry: Retry,
   * }```
   */
  failure?: Failure;
}

const calculateRate = (completed: number, start: Date, total: number): string => {
  if (!completed) {
    return 'calculating';
  }

  if (completed === 100) {
    return 'Completed';
  }

  const elapsed = (Date.now() - start.getTime()) / 1000;
  const bitsPerSec = (8 * total * (completed / 100)) / elapsed;

  const kiloBitsPerSec = bitsPerSec / 1000;

  if (kiloBitsPerSec < 1000) {
    return `${Math.round(kiloBitsPerSec)} kb/sec`;
  }

  const megaBitsPerSec = kiloBitsPerSec / 1000;

  return `${Math.round(megaBitsPerSec)} mb/sec`;
};

export const ProgressListItem: FC<ProgressItemListProps> = ({ subject, progress, rate, failure, role }) => {
  const hasFailed = failure !== undefined;
  const cleanProgress = Math.max(Math.min(progress, 100), 0);
  const progressPercentage = hasFailed ? 0 : Math.round(cleanProgress);

  return (
    <ProgressListItemElement data-testid="ProgressListItem" role={role}>
      <TextElement>{subject}</TextElement>
      <ProgressLineContainer>
        <ProgressLineFill hasFailed={hasFailed} fill={progressPercentage} data-testid="ProgressLineFill" />
      </ProgressLineContainer>
      <ProgressContainer>
        <TextElement>{progressPercentage}%</TextElement>
        {hasFailed && (
          <TextElement>
            {failure.message}
            {failure.retry && (
              <>
                {' '}
                <LinkButton onClick={failure.retry.callback}>{failure.retry?.message ?? 'Retry'}</LinkButton>
              </>
            )}
          </TextElement>
        )}
        {!hasFailed && rate && (
          <TextElement data-testid="RateElement">
            {calculateRate(cleanProgress, rate.start, rate.totalBytes)}
          </TextElement>
        )}
      </ProgressContainer>
    </ProgressListItemElement>
  );
};

ProgressListItem.defaultProps = { role: 'listitem' };
